import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  GraduationCap,
  Building2,
  Landmark,
  Heart,
  Users,
  ArrowRight,
  TrendingUp,
  Shield,
  Zap,
} from "lucide-react";

export default function ForOrganizations() {
  const verticals = [
    {
      icon: GraduationCap,
      title: "Universities & Career Centers",
      description: "Empower students and alumni with cutting-edge job search tools",
      href: "/organizations/universities",
      highlight: "Student Success",
    },
    {
      icon: Building2,
      title: "Corporate Outplacement",
      description: "Support transitioning employees with world-class resources",
      href: "/organizations/corporate-outplacement",
      highlight: "Career Transition",
    },
    {
      icon: Landmark,
      title: "Government Agencies",
      description: "Help citizens find meaningful employment faster",
      href: "/organizations/government",
      highlight: "FISMA Compliant",
    },
    {
      icon: Heart,
      title: "Non-Profit Organizations",
      description: "Maximize impact with special non-profit pricing",
      href: "/organizations/non-profits",
      highlight: "Up to 50% Discount",
    },
    {
      icon: Users,
      title: "Workforce Development",
      description: "Accelerate placement rates with AI-powered tools",
      href: "/organizations/workforce-development",
      highlight: "Training Programs",
    },
  ];

  const stats = [
    { number: "AI-Powered", label: "Career Tools" },
    { number: "Enterprise", label: "Security & Support" },
    { number: "Custom", label: "Integration & Branding" },
    { number: "Proven", label: "Results & Analytics" },
  ];

  return (
    <>
      <Helmet>
        <title>For Organizations | LoopBaan - Enterprise Job Search Solutions</title>
        <meta
          name="description"
          content="Empower your organization with LoopBaan's enterprise job search platform. Solutions for universities, companies, government, non-profits, and workforce development."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <Shield className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Enterprise Solutions</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Empower Your Organization
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                With AI-Powered Career Tools
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Help your students, employees, or community members land their dream jobs faster with LoopBaan&apos;s enterprise platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Enterprise Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Verticals Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Solutions For Every Organization
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Tailored solutions designed for your specific needs and goals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {verticals.map((vertical, idx) => (
              <Link key={idx} to={vertical.href}>
                <Card className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/10 h-full group cursor-pointer">
                  <div className="flex flex-col h-full">
                    <div className="p-3 bg-teal-500/10 rounded-lg w-fit mb-4 group-hover:bg-teal-500/20 transition-colors">
                      <vertical.icon className="h-8 w-8 text-teal-400" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-teal-400 transition-colors">
                      {vertical.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-4 flex-grow">{vertical.description}</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <span className="text-teal-400 font-semibold text-sm">{vertical.highlight}</span>
                      <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Organizations Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/10 rounded-full mb-4">
                  <TrendingUp className="h-8 w-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Proven Results
                </h3>
                <p className="text-gray-400">
                  Faster job placement with our AI-powered tools
                </p>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/10 rounded-full mb-4">
                  <Shield className="h-8 w-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Enterprise Security
                </h3>
                <p className="text-gray-400">
                  SOC 2 certified with bank-level encryption and compliance
                </p>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/10 rounded-full mb-4">
                  <Zap className="h-8 w-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Easy Integration
                </h3>
                <p className="text-gray-400">
                  Seamless setup with your existing systems in minutes
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Your Organization?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start helping your people succeed with LoopBaan&apos;s enterprise platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Schedule a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
